import { App, DirectiveBinding } from 'vue'

interface CopyDirectiveBinding extends Omit<DirectiveBinding, 'value'> {
  value?: string | (() => string)
}

export default {
  install(app: App) {
    app.directive('double-click-copy', {
      mounted(el: HTMLElement, binding: CopyDirectiveBinding) {
        let clickCount = 0
        let timer: NodeJS.Timeout | null = null

        const handleClick = () => {
          clickCount++

          if (timer) clearTimeout(timer)

          if (clickCount === 2) {
            copyText(binding, el)
            clickCount = 0
          } else {
            timer = setTimeout(() => clickCount = 0, 300)
          }
        }

        const copyText = (binding: CopyDirectiveBinding, el: HTMLElement) => {
          const text = binding.value
            ? (typeof binding.value === 'function' ? binding.value() : binding.value)
            : el.textContent?.trim() || ''

          if (!text) {
            console.warn('v-double-click-copy: No text content to copy')
            return
          }

          if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text)
              .then(() => showFeedback(el, text))
              .catch(() => fallbackCopy(text, el))
          } else {
            fallbackCopy(text, el)
          }
        }

        const fallbackCopy = (text: string, el: HTMLElement) => {
          const textarea = document.createElement('textarea')
          textarea.value = text
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          textarea.select()

          try {
            const successful = document.execCommand('copy')
            if (successful) {
              showFeedback(el, text)
            } else {
              console.error('v-double-click-copy: Fallback copy failed')
            }
          } catch (err) {
            console.error('v-double-click-copy: Fallback copy error:', err)
          } finally {
            document.body.removeChild(textarea)
          }
        }

        const showFeedback = (el: HTMLElement, copiedText: string) => {
          // 获取元素在视口中的位置
          const rect = el.getBoundingClientRect()
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop

          // 创建提示元素并直接添加到body，避免被父级overflow:hidden影响 
          const tipElement = document.createElement('div')
          tipElement.className = 'copy-success-tip'
          tipElement.textContent = `copy successful: ${copiedText}`

          // 设置提示样式 - 使用fixed定位相对于视口
          Object.assign(tipElement.style, {
            position: 'fixed',
            top: `${rect.top + scrollTop - 50}px`,
            left: `${rect.left + rect.width / 2}px`,
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            zIndex: '9999',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            animation: 'fadeInOut 1s ease-in-out'
          })

          // 添加到body而不是el，避免被父级overflow:hidden剪裁
          document.body.appendChild(tipElement)

          // 添加淡入淡出动画
          const style = document.createElement('style')
          style.textContent = `
            @keyframes fadeInOut {
              0% { opacity: 0; transform: translateX(-50%) translateY(10px); }
              20% { opacity: 1; transform: translateX(-50%) translateY(0); }
              80% { opacity: 1; transform: translateX(-50%) translateY(0); }
              100% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
            }
          `
          document.head.appendChild(style)

          // 1秒后移除提示元素和样式
          setTimeout(() => {
            if (tipElement.parentNode) {
              tipElement.remove()
            }
            if (style.parentNode) {
              style.remove()
            }
          }, 1000)
        }

        // 添加鼠标悬浮样式
        const addHoverStyles = () => {
          el.style.transition = 'all 0.3s ease'

          const cursor = el.style.cursor
            ; (el as any)._originalCursor = cursor;
          const fontWeight = el.style.fontWeight
            ; (el as any)._originalFontWeight = fontWeight;
          const title = el.title
            ; (el as any)._originalTitle = title;

        }
        // 初始化悬浮样式
        addHoverStyles()

        // 鼠标悬浮效果
        const handleMouseEnter = () => {
          el.style.cursor = 'copy'
          el.style.fontWeight = '700'
          el.title = `Double click to copy ${(el as any)._originalTitle || ''}`
        }

        const handleMouseLeave = () => {
          if ((el as any)._originalCursor) {
            el.style.cursor = (el as any)._originalCursor
          } else {
            el.style.removeProperty('cursor')
          }
          if ((el as any)._originalFontWeight) {
            el.style.fontWeight = (el as any)._originalFontWeight
          } else {
            el.style.removeProperty('font-weight')
          }
          if ((el as any)._originalTitle) {
            el.title = (el as any)._originalTitle
          } else {
            el.removeAttribute('title')
          }
        }
        // 添加事件监听器
        el.addEventListener('click', handleClick)
        el.addEventListener('mouseenter', handleMouseEnter)
        el.addEventListener('mouseleave', handleMouseLeave)

          // 存储清理函数
          ; (el as any)._copyHandler = handleClick
          ; (el as any)._mouseEnterHandler = handleMouseEnter
          ; (el as any)._mouseLeaveHandler = handleMouseLeave
      },

      beforeUnmount(el: HTMLElement) {
        // 移除事件监听器
        if ((el as any)._copyHandler) {
          el.removeEventListener('click', (el as any)._copyHandler)
        }
        if ((el as any)._mouseEnterHandler) {
          el.removeEventListener('mouseenter', (el as any)._mouseEnterHandler)
        }
        if ((el as any)._mouseLeaveHandler) {
          el.removeEventListener('mouseleave', (el as any)._mouseLeaveHandler)
        }

        // 清理可能存在的提示元素
        const existingTips = document.querySelectorAll('.copy-success-tip')
        existingTips.forEach(tip => tip.remove())
      }
    })
  }
}
